import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader2,
  Rocket,
  XCircle,
} from "lucide-react";

import {
  DEFAULT_STRATEGY,
  POOL_PHASE_LABELS,
  POOL_PHASES,
  poolPhaseStepState,
  useAbortRollout,
  useRollouts,
  useStartRollout,
  type ActiveRollout,
  type BlueGreenStrategy,
  type PoolPlanInput,
  type PoolRolloutPlan,
  type ReplicaStatus,
  type RolloutReplicaProgressOut,
  type RolloutScope,
} from "@/api/local/rollout";
import {
  diffWorkloadSpec,
  type WorkloadDiff,
  type WorkloadSpec,
} from "@/api/local/workload";
import { ApiError } from "@/api/client";
import { Modal } from "@/components/Modal";
import { Pill, type PillTone } from "@/components/Pill";
import { WorkloadDiffView } from "@/components/WorkloadDiff";

// Discrete surge-percentage choices the engine actually understands.
// Anything in between would round to the nearest step in the engine
// anyway, and the segmented control below avoids the "native range
// thumb is offset from the track edge" visual confusion (where a
// value of 100 didn't make the thumb visually reach the very right of
// the slider).
const SURGE_PCT_OPTIONS: readonly number[] = [25, 50, 75, 100];

interface BaseProps {
  open: boolean;
  onClose: () => void;
  scope: RolloutScope;
  /** Title affix, e.g. service.name or pool.sky_service_name. */
  scopeLabel: string;
  serviceId: number;
  /** Only set when scope === "pool". */
  poolId?: number;
  /** Live workload before this rollout (null on first deploy). */
  source: WorkloadSpec | null;
  /** Target workload to deploy. */
  target: WorkloadSpec;
  /** Per-pool plan that will execute. Pre-built by caller. */
  plan: PoolPlanInput[];
  /**
   * If true, this is the *initial* deploy of a draft service. The
   * modal labels itself accordingly, the "Confirm" button reads
   * "Activate & deploy", and `onComplete` (passed from the caller)
   * gets called once the rollout simulator finishes — the caller
   * uses it to PATCH `enabled: true` on the controller and clear
   * the local draft marker.
   */
  firstDeploy?: boolean;
  /** Post-rollout hook. Wired into the rollout simulator's onComplete. */
  onComplete?: () => void | Promise<void>;
}

export function RolloutModal(props: BaseProps) {
  const {
    open,
    onClose,
    scope,
    scopeLabel,
    serviceId,
    poolId,
    source,
    target,
    plan,
    firstDeploy,
    onComplete,
  } = props;
  const rolloutDoc = useRollouts();
  const active = rolloutDoc.active;
  const [strategy, setStrategy] = useState<BlueGreenStrategy>(DEFAULT_STRATEGY);
  const [submitting, setSubmitting] = useState(false);
  // Last submission error, surfaced inline in the modal. Cleared on
  // each retry. Replaces the previous `window.alert` which threw away
  // FastAPI's `detail` (so 409s rendered as a bare "HTTP 409").
  const [submitError, setSubmitError] = useState<RolloutError | null>(null);
  const startRollout = useStartRollout();

  const diffs = useMemo<WorkloadDiff[]>(
    () => diffWorkloadSpec(source, target),
    [source, target],
  );

  // Determine which lifecycle this modal is showing:
  // - "preflight": no rollout running, show diff + plan + strategy + Confirm
  // - "running": rollout in flight, show progress
  // - "completed/aborted": rollout finished, show summary; modal can be closed
  const isThisRollout =
    active &&
    active.service_id === serviceId &&
    active.scope === scope &&
    (scope === "service" || active.pool_id === poolId);

  const showProgress = !!isThisRollout;

  const totalReplicas = plan.reduce((acc, p) => acc + p.replicas, 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        firstDeploy
          ? `First deploy · ${scopeLabel}`
          : `Roll out · ${scope} · ${scopeLabel}`
      }
      subtitle={
        showProgress
          ? `${POOL_PHASES.length} phases per pool · always blue/green`
          : `${plan.length} pool${plan.length === 1 ? "" : "s"} · ${totalReplicas} replica${totalReplicas === 1 ? "" : "s"}`
      }
      headerExtras={
        <>
          {firstDeploy && <Pill tone="warn">first deploy</Pill>}
          <Pill tone="brand">blue/green</Pill>
          {showProgress && active && (
            <Pill tone={statePillTone(active.state)}>
              {active.state}
            </Pill>
          )}
        </>
      }
      footer={
        showProgress && active ? (
          <ProgressFooter active={active} onClose={onClose} />
        ) : (
          <PreflightFooter
            disabled={
              submitting ||
              // Empty plan is allowed for service-scope rollouts: it's
              // the "lock the workload in now, attach pools later"
              // workflow. The controller commits the draft -> live
              // promotion (and the first-deploy lifecycle flip) and
              // the engine auto-completes the rollout on the next tick.
              (scope === "pool" && plan.length === 0) ||
              // Refuse to roll if there is literally nothing to bring up.
              // This blocks the (very common) "fresh pool with
              // min_replicas=0 and no desired-count row" case: the
              // controller would otherwise accept the rollout, then sit
              // in `provisioning` until the watchdog fires `failed`.
              (plan.length > 0 && totalReplicas === 0) ||
              (diffs.length === 0 && source !== null && !firstDeploy)
            }
            submitting={submitting}
            firstDeploy={!!firstDeploy}
            onCancel={onClose}
            onConfirm={async () => {
              setSubmitting(true);
              setSubmitError(null);
              try {
                // The controller atomically promotes draft -> live and
                // (for draft services) flips lifecycle_state -> active +
                // enabled = true as part of starting the rollout. The
                // browser no longer needs a separate "promote" PATCH.
                await startRollout({
                  scope,
                  service_id: serviceId,
                  pool_id: poolId,
                  strategy,
                  on_complete: onComplete
                    ? () => onComplete()
                    : undefined,
                });
                // Drop the modal once the controller has acked the
                // rollout. Progress is now rendered inline on each
                // affected pool's Replicas tab (PoolRolloutPanel), so
                // we deliberately don't pin the operator on this
                // modal's ProgressView — the "view rollout" button on
                // the caller surface stays available if they want it.
                onClose();
              } catch (err) {
                setSubmitError(toRolloutError(err));
              } finally {
                setSubmitting(false);
              }
            }}
          />
        )
      }
    >
      {showProgress && active ? (
        <ProgressView active={active} />
      ) : (
        <PreflightView
          diffs={diffs}
          plan={plan}
          strategy={strategy}
          onStrategyChange={setStrategy}
          isFirstRollout={source === null}
          firstDeploy={!!firstDeploy}
          showMaxConcurrency={scope === "service" && plan.length > 1}
          zeroReplicas={plan.length > 0 && totalReplicas === 0}
          submitError={submitError}
          activeRollout={active ?? null}
        />
      )}
    </Modal>
  );
}

// ---- pre-flight (before user clicks Confirm) ----------------------------

interface RolloutError {
  /** HTTP status code, when the failure was an `ApiError`. */
  status?: number;
  /** Human-readable message (server `detail` when present). */
  message: string;
}

function toRolloutError(err: unknown): RolloutError {
  if (err instanceof ApiError) {
    return { status: err.status, message: err.message };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

function PreflightView({
  diffs,
  plan,
  strategy,
  onStrategyChange,
  isFirstRollout,
  firstDeploy,
  showMaxConcurrency,
  zeroReplicas,
  submitError,
  activeRollout,
}: {
  diffs: WorkloadDiff[];
  plan: PoolPlanInput[];
  strategy: BlueGreenStrategy;
  onStrategyChange: (s: BlueGreenStrategy) => void;
  isFirstRollout: boolean;
  firstDeploy: boolean;
  showMaxConcurrency: boolean;
  zeroReplicas: boolean;
  submitError: RolloutError | null;
  activeRollout: ActiveRollout | null;
}) {
  return (
    <>
      {submitError && (
        <SubmitErrorBanner error={submitError} activeRollout={activeRollout} />
      )}
      {firstDeploy && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          <strong>This is the service&rsquo;s very first deploy.</strong>{" "}
          On confirm, BackStage will flip <code>enabled: true</code> on
          the controller and bring up replicas across the pool plan
          below. Until then nothing leaves the database.
        </div>
      )}
      {isFirstRollout && !firstDeploy && (
        <div className="flash" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          First rollout for this scope — there&rsquo;s no previous live
          spec, so the diff lists every field as added.
        </div>
      )}
      {plan.length === 0 && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          <strong>No pools attached yet.</strong> That&rsquo;s OK — this
          rollout will commit the workload as live (and, for a draft
          service, flip <code>enabled: true</code>) and then complete
          immediately. Attach capacity afterwards via{" "}
          <strong>+ pool</strong> on this service&rsquo;s row in the
          tree; the reconciler picks it up using the workload you just
          locked in. Use this if you want to lock in spec before
          choosing where to run it.
        </div>
      )}
      {zeroReplicas && (
        <div
          className="flash"
          style={{
            marginBottom: 16,
            borderColor: "rgba(248,113,113,0.4)",
            background: "var(--danger-soft)",
            color: "var(--danger)",
          }}
        >
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          Every pool in this rollout resolves to{" "}
          <strong>0 target replicas</strong> — nothing would actually
          come up. Raise <code>min_replicas</code> on the pool
          (Settings tab) or pin a non-zero desired count on the
          Autoscaling tab, then come back.
        </div>
      )}

      <Section title="Spec changes">
        <WorkloadDiffView diffs={diffs} />
      </Section>

      <Section title="Per-pool plan">
        <table className="data plan-table">
          <thead>
            <tr>
              <th>Pool</th>
              <th style={{ textAlign: "right" }}>Replicas</th>
              <th>Strategy</th>
            </tr>
          </thead>
          <tbody>
            {plan.map((p) => (
              <tr key={p.pool_id}>
                <td className="mono">{p.pool_name}</td>
                <td className="num">{p.replicas}</td>
                <td>
                  <Pill tone="brand">blue/green</Pill>
                </td>
              </tr>
            ))}
            {plan.length === 0 && (
              <tr>
                <td colSpan={3} className="empty">
                  no pools to roll
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Strategy (blue/green)">
        <div className="form-grid">
          <label>surge %</label>
          <div className="field">
            <div
              role="radiogroup"
              aria-label="surge percentage"
              className="surge-segments"
            >
              {SURGE_PCT_OPTIONS.map((opt) => {
                const active = strategy.surge_pct === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`surge-segments__btn${
                      active ? " is-active" : ""
                    }`}
                    onClick={() =>
                      onStrategyChange({
                        ...strategy,
                        surge_pct: opt,
                      })
                    }
                  >
                    {opt}%
                  </button>
                );
              })}
            </div>
            <div className="field-hint">
              Fraction of pool capacity to bring up as <em>green</em> at
              once. <span className="mono">100%</span> is full B/G; lower
              values trade rollout speed for GPU cost.
            </div>
          </div>
          <label>health grace</label>
          <div className="field">
            <input
              type="number"
              min={0}
              value={strategy.health_check_grace_seconds}
              onChange={(e) =>
                onStrategyChange({
                  ...strategy,
                  health_check_grace_seconds: Number(e.target.value),
                })
              }
              style={{ width: 120 }}
            />
            <div className="field-hint">
              seconds to wait after green is healthy before flipping traffic.
            </div>
          </div>
          <label>drain timeout</label>
          <div className="field">
            <input
              type="number"
              min={0}
              value={strategy.drain_seconds}
              onChange={(e) =>
                onStrategyChange({
                  ...strategy,
                  drain_seconds: Number(e.target.value),
                })
              }
              style={{ width: 120 }}
            />
            <div className="field-hint">
              seconds to let blue requests finish before tearing blue down.
            </div>
          </div>
          {showMaxConcurrency && (
            <>
              <label>max parallel pools</label>
              <div className="field">
                <input
                  type="number"
                  min={1}
                  max={Math.max(1, plan.length)}
                  value={strategy.max_concurrent_pools}
                  onChange={(e) =>
                    onStrategyChange({
                      ...strategy,
                      max_concurrent_pools: Math.max(
                        1,
                        Math.min(plan.length, Number(e.target.value)),
                      ),
                    })
                  }
                  style={{ width: 80 }}
                />
                <div className="field-hint">
                  how many pools may roll simultaneously.
                </div>
              </div>
            </>
          )}
        </div>
      </Section>
    </>
  );
}

function PreflightFooter({
  disabled,
  submitting,
  firstDeploy,
  onCancel,
  onConfirm,
}: {
  disabled: boolean;
  submitting: boolean;
  firstDeploy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <button type="button" className="btn" onClick={onCancel} disabled={submitting}>
        Cancel
      </button>
      <div className="right" />
      <button
        type="button"
        className="btn btn--primary"
        onClick={onConfirm}
        disabled={disabled}
      >
        {submitting ? <Loader2 /> : <Rocket />}
        {submitting
          ? "saving spec…"
          : firstDeploy
            ? "Activate & deploy (blue/green)"
            : "Roll out (blue/green)"}
      </button>
    </>
  );
}

// ---- progress view (during a rollout) -----------------------------------

function ProgressView({ active }: { active: ActiveRollout }) {
  return (
    <>
      <Section title="Per-pool progress">
        <div className="rollout-progress">
          {active.plan.map((p) => (
            <PoolProgress key={p.pool_id} plan={p} />
          ))}
        </div>
      </Section>
    </>
  );
}

function PoolProgress({ plan }: { plan: PoolRolloutPlan }) {
  const phaseLabel = POOL_PHASE_LABELS[plan.phase];
  const replicaRows = plan.replicas_progress ?? [];
  const greenRows = replicaRows.filter((r) => r.role === "green");
  const blueRows = replicaRows.filter((r) => r.role === "blue");
  // "M/N ready" — show whenever the server snapshotted a non-zero
  // target_ready (i.e. once we're past `queued`). During `queued`
  // target_ready is 0 and we suppress the readout to avoid a noisy
  // "0/0 ready".
  const showReadyCount = plan.target_ready > 0;

  return (
    <div className="rollout-pool">
      <div className="rollout-pool__head">
        <div className="rollout-pool__name mono">{plan.pool_name}</div>
        <div className="rollout-pool__phase">
          {plan.phase === "complete" ? (
            <Pill tone="ok">
              <CheckCircle2 size={11} /> {phaseLabel}
            </Pill>
          ) : plan.phase === "failed" ? (
            <Pill tone="danger">
              <XCircle size={11} /> {phaseLabel}
            </Pill>
          ) : plan.phase === "queued" ? (
            <Pill tone="neutral">{phaseLabel}</Pill>
          ) : (
            <Pill tone="info" pulse>
              <Loader2 size={11} /> {phaseLabel}
            </Pill>
          )}
        </div>
        {showReadyCount && (
          <div className="rollout-pool__count mono muted">
            {plan.ready_green}/{plan.target_ready} ready
          </div>
        )}
      </div>
      <div className="rollout-pool__steps">
        {POOL_PHASES.map((ph) => (
          <div
            key={ph}
            className={`rollout-step rollout-step--${poolPhaseStepState(plan.phase, ph)}`}
            title={POOL_PHASE_LABELS[ph]}
          >
            <span className="rollout-step__dot" />
            <span className="rollout-step__label">{ph}</span>
          </div>
        ))}
      </div>

      {(greenRows.length > 0 || blueRows.length > 0) && (
        <div className="rollout-replicas">
          {greenRows.length > 0 && (
            <ReplicaGroup
              label="green (new)"
              tone="green"
              replicas={greenRows}
            />
          )}
          {blueRows.length > 0 && (
            <ReplicaGroup
              label="blue (existing)"
              tone="blue"
              replicas={blueRows}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ReplicaGroup({
  label,
  tone,
  replicas,
}: {
  label: string;
  tone: "green" | "blue";
  replicas: RolloutReplicaProgressOut[];
}) {
  return (
    <div className={`rollout-replica-group rollout-replica-group--${tone}`}>
      <div className="rollout-replica-group__label muted">{label}</div>
      <div className="rollout-replica-group__rows">
        {replicas.map((r) => (
          <ReplicaRow key={r.replica_id} replica={r} />
        ))}
      </div>
    </div>
  );
}

function ReplicaRow({ replica }: { replica: RolloutReplicaProgressOut }) {
  const statusTone = replicaStatusTone(replica.status);
  const showPulse =
    replica.status === "provisioning" ||
    replica.status === "pending" ||
    replica.status === "draining" ||
    replica.status === "draining_long_lived";
  const idLabel = replica.sky_replica_id
    ? `replica#${replica.sky_replica_id}`
    : `replica#${replica.replica_id}`;
  const place = [replica.region, replica.instance_type]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={`rollout-replica rollout-replica--${replica.role}`}>
      <div className="rollout-replica__head">
        <div className="rollout-replica__id mono">{idLabel}</div>
        <Pill tone={statusTone} pulse={showPulse}>
          {replica.sky_status_raw || replica.status}
        </Pill>
        {place && (
          <div className="rollout-replica__place mono muted">{place}</div>
        )}
      </div>
      {replica.status_message && (
        <div className="rollout-replica__msg muted" title={replica.status_message}>
          {replica.status_message}
        </div>
      )}
    </div>
  );
}

function replicaStatusTone(s: ReplicaStatus): PillTone {
  if (s === "ready") return "ok";
  if (s === "failed") return "danger";
  if (s === "not_ready") return "warn";
  if (s === "shutting_down") return "warn";
  if (s === "draining" || s === "draining_long_lived") return "warn";
  return "info";
}

function ProgressFooter({
  active,
  onClose,
}: {
  active: ActiveRollout;
  onClose: () => void;
}) {
  const abortRollout = useAbortRollout();
  if (active.state === "running" || active.state === "pending") {
    return (
      <>
        <button
          type="button"
          className="btn btn--danger"
          onClick={() => {
            void abortRollout(active.id);
          }}
        >
          <XCircle />
          Abort (leave blue alive)
        </button>
        <div className="right" />
        <button type="button" className="btn" onClick={onClose}>
          Close (continues in background)
        </button>
      </>
    );
  }
  return (
    <>
      <div className="right" />
      <button type="button" className="btn btn--primary" onClick={onClose}>
        <Check />
        Done
      </button>
    </>
  );
}

// ---- error banner --------------------------------------------------------

/**
 * Inline error banner shown above the preflight body when
 * `POST /rollouts` fails. Replaces a previous `window.alert(err.message)`
 * which dropped FastAPI's `detail` (so 409s rendered as a bare
 * "HTTP 409"). For 409 specifically — "another rollout is already in
 * progress" — we also surface what's running and which scope it
 * targets, so the operator knows whether they need to wait or abort.
 */
function SubmitErrorBanner({
  error,
  activeRollout,
}: {
  error: RolloutError;
  activeRollout: ActiveRollout | null;
}) {
  const isConflict = error.status === 409;
  return (
    <div
      className={`flash flash--danger`}
      style={{ marginBottom: 16 }}
      role="alert"
    >
      <AlertTriangle
        style={{
          width: 14,
          height: 14,
          verticalAlign: "middle",
          marginRight: 6,
        }}
      />
      <strong>
        {isConflict
          ? "Another rollout is already in progress."
          : "Could not start rollout."}
      </strong>{" "}
      {error.message}
      {isConflict && activeRollout && (
        <div style={{ marginTop: 6 }} className="muted">
          In flight: rollout #{activeRollout.id} ·{" "}
          <span className="mono">{activeRollout.scope}</span>
          {activeRollout.scope === "pool" && activeRollout.pool_id != null && (
            <>
              {" "}· pool <span className="mono">#{activeRollout.pool_id}</span>
            </>
          )}{" "}
          · state <span className="mono">{activeRollout.state}</span>. Wait for
          it to finish, or abort it from its own pool / service tab before
          retrying.
        </div>
      )}
    </div>
  );
}

// ---- helpers -------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="modal-section">
      <div className="modal-section__title">{title}</div>
      <div className="modal-section__body">{children}</div>
    </div>
  );
}

function statePillTone(s: ActiveRollout["state"]): PillTone {
  if (s === "completed") return "ok";
  if (s === "aborted") return "danger";
  return "info";
}

